import { Entity } from '@api';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { BrandAndProductDrawer } from '@terminal/modules/brand-and-product-drawer/BrandAndProductDrawer';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { appPaths } from '@terminal/routes/app-paths';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { useBrandAndProductDrawer } from '@terminal/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useSearchParams } from 'react-router';
import { ConnectionsEmptyOrError } from '../shared/connections-error-or-empty/ConnectionsEmptyOrError';
import { RowItem } from '../shared/row-item/RowItem';
import styles from './BrandsAndProducts.module.scss';

export interface BrandsAndProductsProps {
  entity: Entity;
}

export const BrandsAndProducts = ({ entity }: BrandsAndProductsProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const {
    data: brandsAndProducts = [],
    isFetching,
    error,
  } = useBrandsAndProducts(entity.tag);
  const { openDrawer } = useBrandAndProductDrawer({ entityTag: entity.tag });

  if (error && error.status !== httpCode.notFound) {
    throw error;
  }

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag: entity.tag,
    searchParams,
    subtab: KnowledgeGraphTabType.BrandsAndProducts,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const topItems = [...brandsAndProducts]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3);

  if (!isFetching && topItems.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {t('widget:assetOverview.knowledgeGraph.subtab.brandsAndProducts')}
        </div>
        <ConnectionsEmptyOrError
          variant="empty"
          title={t(
            'widget:assetOverview.knowledgeGraphRevamp.empty.brandsAndProducts'
          )}
          layout="fixed"
        />
      </div>
    );
  }

  return (
    <div>
      <SectionTitle
        title={t(
          'widget:assetOverview.knowledgeGraph.subtab.brandsAndProducts'
        )}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('brandAndProducts')}
      />
      {isFetching ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <RowItem key={idx} rowType="brand" isLoading />
          ))}
        </div>
      ) : (
        <div>
          {topItems.map(item => (
            <RowItem
              key={item.relationship.id}
              rowType="brand"
              rank={item.relationship.rank}
              onClick={() => openDrawer(item.product)}
            >
              <span className={styles.name}>{item.product}</span>
            </RowItem>
          ))}
        </div>
      )}
      <BrandAndProductDrawer entity={entity} />
    </div>
  );
};
